import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllEntriesByUsername,
  deleteEntry,
  verifyPin,
  getPrivateEntriesForPartner,
  getProfile,
  getSubscriptionStatus
} from '../services/api';
import EntryCard from './EntryCard';
import PinPrompt from './PinPrompt';
import { SkeletonEntryCard } from './SkeletonCard';
import './ViewEntries.css';

function ViewEntries() {
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Undo delete — Premium only
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const undoTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  // This ref tracks whether undo was clicked — prevents backend delete after undo
  const undoCancelledRef = useRef(false);

  const navigate = useNavigate();

  const [showPrivateDropdown, setShowPrivateDropdown] = useState(false);
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [selectedPrivatePartner, setSelectedPrivatePartner] = useState('');
  const [selectedGender, setSelectedGender] = useState('male');
  const [privateEntries, setPrivateEntries] = useState([]);
  const [viewingPrivate, setViewingPrivate] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchEntries();
    fetchSubscription();

    return () => {
      clearUndoTimers();
    };
  }, []);

  useEffect(() => {
    filterEntries();
  }, [entries, selectedPartner, pendingDeleteId, searchQuery]);

  const fetchSubscription = async () => {
    try {
      const username = localStorage.getItem('username');
      const sub = await getSubscriptionStatus(username);
      setIsPremium(sub.isPremium);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const fetchProfile = async () => {
    try {
      const username = localStorage.getItem('username');
      if (!username) { navigate('/login'); return; }
      const profileData = await getProfile(username);
      setProfile(profileData);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchEntries = async () => {
    try {
      const username = localStorage.getItem('username');
      if (!username) { navigate('/login'); return; }
      const data = await getAllEntriesByUsername(username);
      setEntries(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching entries:', err);
      setError('Failed to load entries');
      setLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = entries;
    if (selectedPartner !== 'All') {
      filtered = filtered.filter(entry => entry.partnerName === selectedPartner);
    }
    if (pendingDeleteId) {
      filtered = filtered.filter(entry => entry.id !== pendingDeleteId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        (entry.description && entry.description.toLowerCase().includes(q)) ||
        (entry.category && entry.category.toLowerCase().includes(q))
      );
    }
    setFilteredEntries(filtered);
  };

  const getPartnerNames = () => {
    return [...new Set(entries.map(entry => entry.partnerName))];
  };

  const clearUndoTimers = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };

  // Called by EntryCard after user confirms deletion in the modal
  // onDone callback resets the deleting spinner in the card
  const handleDelete = (id, onDone) => {
    if (isPremium) {
      // Premium: show undo for 5 seconds, ONLY delete from backend if undo not clicked
      if (pendingDeleteId) {
        // There's already a pending delete — execute it immediately before starting new one
        executePendingDelete(pendingDeleteId);
      }

      // Reset undo cancelled flag
      undoCancelledRef.current = false;

      // Hide entry from view immediately (optimistic UI)
      setPendingDeleteId(id);
      setUndoCountdown(5);
      if (onDone) onDone();

      countdownIntervalRef.current = setInterval(() => {
        setUndoCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      undoTimerRef.current = setTimeout(() => {
        // Only delete from backend if undo was NOT clicked
        if (!undoCancelledRef.current) {
          executePendingDelete(id);
        }
      }, 5000);
    } else {
      // Free tier: delete from backend immediately, remove from state, no undo
      const username = localStorage.getItem('username');
      deleteEntry(id, username)
        .then(response => {
          if (response && response.success) {
            setEntries(prev => prev.filter(entry => entry.id !== id));
            setPrivateEntries(prev => prev.filter(entry => entry.id !== id));
            showSuccess('Entry deleted');
          } else {
            showError('Failed to delete entry. Please try again.');
          }
        })
        .catch(err => {
          console.error('Error deleting entry:', err);
          showError('Failed to delete entry. Please try again.');
        })
        .finally(() => {
          if (onDone) onDone();
        });
    }
  };

  const executePendingDelete = async (id) => {
    clearUndoTimers();
    setPendingDeleteId(null);
    setUndoCountdown(0);

    try {
      const username = localStorage.getItem('username');
      const response = await deleteEntry(id, username);
      if (response && response.success) {
        // Remove from state permanently
        setEntries(prev => prev.filter(entry => entry.id !== id));
        setPrivateEntries(prev => prev.filter(entry => entry.id !== id));
        showSuccess('Entry deleted permanently');
      } else {
        // Backend delete failed — restore entry in view by re-fetching
        showError('Failed to delete entry. Please try again.');
        fetchEntries();
      }
    } catch (err) {
      console.error('Error deleting entry:', err);
      showError('Failed to delete entry. Please try again.');
      fetchEntries();
    }
  };

  const handleUndoDelete = () => {
    if (!pendingDeleteId) return;

    // Set the cancelled flag so the setTimeout callback skips the backend delete
    undoCancelledRef.current = true;

    clearUndoTimers();
    setPendingDeleteId(null);
    setUndoCountdown(0);

    showSuccess('Deletion cancelled — entry restored');
  };

  const handleEdit = (id) => {
    navigate(`/edit-entry/${id}`);
  };

  const handlePrivacyToggle = (id, isPrivate) => {
    setEntries(prev =>
      prev.map(entry => entry.id === id ? { ...entry, isPrivate } : entry)
    );
    if (!isPrivate) {
      setPrivateEntries(prev => prev.filter(entry => entry.id !== id));
    } else {
      setPrivateEntries(prev =>
        prev.map(entry => entry.id === id ? { ...entry, isPrivate } : entry)
      );
    }
    showSuccess(isPrivate ? 'Entry marked as private' : 'Entry made public');
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 3000);
  };

  const determineGender = (partnerName) => {
    if (!profile) return 'male';
    if (profile.maleName && partnerName === profile.maleName) return 'male';
    if (profile.femaleName && partnerName === profile.femaleName) return 'female';
    return 'male';
  };

  const handlePrivatePartnerSelect = (partnerName) => {
    setSelectedPrivatePartner(partnerName);
    setShowPrivateDropdown(false);
    const gender = determineGender(partnerName);
    setSelectedGender(gender);
    setShowPinPrompt(true);
  };

  const handlePinVerify = async (pin, gender) => {
    try {
      const username = localStorage.getItem('username');
      const sub = await getSubscriptionStatus(username);
      if (!sub.isPremium) {
        setShowPinPrompt(false);
        setSelectedPrivatePartner('');
        showError('🔒 Viewing private entries requires an active Premium plan. Upgrade on the Plus page.');
        return false;
      }
      const response = await verifyPin(username, pin, gender);
      if (response.valid) {
        const privateEntriesData = await getPrivateEntriesForPartner(
          username, selectedPrivatePartner, username, pin
        );
        setPrivateEntries(privateEntriesData);
        setViewingPrivate(true);
        setShowPinPrompt(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  };

  const handleBackToAllEntries = () => {
    setViewingPrivate(false);
    setPrivateEntries([]);
    setSelectedPrivatePartner('');
  };

  const handleGenderChange = (newGender) => {
    setSelectedGender(newGender);
  };

  if (loading) {
    return (
      <div className="view-entries-container">
        <div className="view-entries-header">
          <h2>My Journal Entries</h2>
        </div>
        <div className="entries-grid">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <SkeletonEntryCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-entries-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchEntries} className="retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  const publicEntries = filteredEntries.filter(entry => !entry.isPrivate);
  const displayPrivateEntries = privateEntries.filter(entry => entry.id !== pendingDeleteId);
  const totalPrivateEntries = entries.filter(entry => entry.isPrivate).length;

  return (
    <div className="view-entries-container">
      {successMessage && (
        <div className="success-message">✓ {successMessage}</div>
      )}
      {errorMessage && (
        <div className="error-message">
          ✗ {errorMessage}
          {errorMessage.includes('Plus page') && (
            <button className="upgrade-prompt-btn" onClick={() => navigate('/plus')}>
              ⭐ Upgrade to Premium
            </button>
          )}
        </div>
      )}

      {/* Undo banner — Premium only, only when countdown is active */}
      {isPremium && pendingDeleteId && undoCountdown > 0 && (
        <div className="undo-banner">
          <span className="undo-message">
            Entry will be deleted in <strong>{undoCountdown}s</strong>
          </span>
          <button className="undo-button" onClick={handleUndoDelete}>
            ↶ Undo
          </button>
        </div>
      )}

      <div className="view-entries-header">
        <h2>{viewingPrivate ? `Private Entries - ${selectedPrivatePartner}` : 'My Journal Entries'}</h2>

        <div className="search-bar-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        {!viewingPrivate && (
          <div className="header-actions">
            <div className="filter-section">
              <label htmlFor="partner-filter">Filter by Partner:</label>
              <select
                id="partner-filter"
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
                className="partner-select"
              >
                <option value="All">All Partners</option>
                {getPartnerNames().map(partner => (
                  <option key={partner} value={partner}>{partner}</option>
                ))}
              </select>
            </div>

            <div className="private-entries-dropdown-wrapper">
              <button
                className="private-entries-button"
                onClick={() => setShowPrivateDropdown(!showPrivateDropdown)}
              >
                🔒 Private Entries {totalPrivateEntries > 0 && `(${totalPrivateEntries})`} ▼
              </button>

              {showPrivateDropdown && (
                <div className="private-entries-dropdown">
                  <div className="dropdown-header">Select Partner:</div>
                  {getPartnerNames().length > 0 ? (
                    getPartnerNames().map(name => (
                      <button
                        key={name}
                        className="dropdown-item"
                        onClick={() => handlePrivatePartnerSelect(name)}
                      >
                        {name}
                      </button>
                    ))
                  ) : (
                    <div className="dropdown-empty">No partners found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {viewingPrivate && (
          <button className="back-to-all-button" onClick={handleBackToAllEntries}>
            ← Back to All Entries
          </button>
        )}
      </div>

      <div className="entries-grid">
        {viewingPrivate ? (
          displayPrivateEntries.length > 0 ? (
            displayPrivateEntries.map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onPrivacyToggle={handlePrivacyToggle}
                username={localStorage.getItem('username')}
              />
            ))
          ) : (
            <div className="no-entries">
              <p>No private entries found for {selectedPrivatePartner}</p>
            </div>
          )
        ) : (
          publicEntries.length > 0 ? (
            publicEntries.map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onPrivacyToggle={handlePrivacyToggle}
                username={localStorage.getItem('username')}
              />
            ))
          ) : (
            <div className="no-entries">
              <p>No entries found</p>
              {totalPrivateEntries > 0 && (
                <p className="private-hint">
                  You have {totalPrivateEntries} private entries. Click "🔒 Private Entries" to view them.
                </p>
              )}
              <button onClick={() => navigate('/add-entry')} className="add-first-entry">
                Add Your First Entry
              </button>
            </div>
          )
        )}
      </div>

      <PinPromptWithGenderSelect
        isOpen={showPinPrompt}
        onClose={() => {
          setShowPinPrompt(false);
          setSelectedPrivatePartner('');
        }}
        onVerify={handlePinVerify}
        partnerName={selectedPrivatePartner}
        gender={selectedGender}
        onGenderChange={handleGenderChange}
      />
    </div>
  );
}

function PinPromptWithGenderSelect({ isOpen, onClose, onVerify, partnerName, gender, onGenderChange }) {
  const [currentGender, setCurrentGender] = useState(gender);

  useEffect(() => {
    setCurrentGender(gender);
  }, [gender]);

  if (!isOpen) return null;

  return (
    <div className="pin-prompt-overlay" onClick={onClose}>
      <div className="pin-prompt-modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="gender-toggle-container">
          <p className="gender-toggle-label">Select which PIN to use:</p>
          <div className="gender-toggle-buttons">
            <button
              type="button"
              className={`gender-toggle-btn ${currentGender === 'male' ? 'active' : ''}`}
              onClick={() => { setCurrentGender('male'); onGenderChange('male'); }}
            >
              ♂️ Male PIN
            </button>
            <button
              type="button"
              className={`gender-toggle-btn ${currentGender === 'female' ? 'active' : ''}`}
              onClick={() => { setCurrentGender('female'); onGenderChange('female'); }}
            >
              ♀️ Female PIN
            </button>
          </div>
        </div>
        <PinPrompt
          isOpen={true}
          onClose={onClose}
          onVerify={(pin) => onVerify(pin, currentGender)}
          partnerName={partnerName}
          gender={currentGender}
        />
      </div>
    </div>
  );
}

export default ViewEntries;